import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Clock, CheckCircle, ArrowSquareOut } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix default icon path (Webpack quirk)
const defaultIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
const doneIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa('<svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41"><path fill="#16a34a" stroke="#fff" stroke-width="1" d="M12.5 0C5.6 0 0 5.6 0 12.5 0 21.9 12.5 41 12.5 41S25 21.9 25 12.5C25 5.6 19.4 0 12.5 0z"/><circle cx="12.5" cy="12.5" r="6" fill="#fff"/></svg>'),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

// Réunion island center
const DEFAULT_CENTER = [-21.115, 55.536];

const fmtDate = (d) => (d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—');

const InterventionMap = () => {
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coords, setCoords] = useState({});
  const [geocoding, setGeocoding] = useState(0);
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/interventions`, { withCredentials: true });
        setAllItems(r.data);
      } catch (e) {
        // silent
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Filter by selectedDate
  useEffect(() => {
    if (!allItems.length) { setItems([]); return; }
    const start = new Date(`${selectedDate}T00:00:00`);
    const end = new Date(`${selectedDate}T23:59:59`);
    const filtered = allItems.filter(it => {
      if (!it.scheduled_date) return false;
      const d = new Date(it.scheduled_date);
      return d >= start && d <= end;
    });
    setItems(filtered);
  }, [selectedDate, allItems]);

  // Geocode addresses using Nominatim (OpenStreetMap)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const it of items) {
        if (cancelled) break;
        if (coords[it.id]) continue;
        if (!it.client_address) continue;
        setGeocoding(g => g + 1);
        try {
          const q = encodeURIComponent(`${it.client_address}, Réunion, France`);
          const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${q}`, {
            headers: { 'Accept': 'application/json' },
          });
          const data = await res.json();
          if (data && data.length > 0) {
            setCoords(prev => ({ ...prev, [it.id]: { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) } }));
          }
        } catch (e) {
          // ignore
        } finally {
          setGeocoding(g => g - 1);
        }
        // Be nice to Nominatim — rate-limit ourselves
        await new Promise(r => setTimeout(r, 1100));
      }
    })();
    return () => { cancelled = true; };
  }, [items]);

  const markers = useMemo(() => items.map(it => ({ it, pos: coords[it.id] })).filter(x => x.pos), [items, coords]);

  return (
    <div className="App">
      <Sidebar />
      <div className="main-content" data-testid="intervention-map-page">
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Carte des interventions</h1>
          <p className="text-slate-600">Sélectionnez une date pour visualiser les interventions correspondantes sur la carte</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 flex flex-col md:flex-row items-start md:items-center gap-3">
          <label className="text-sm font-medium text-slate-700">Date :</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            data-testid="map-date-picker"
            className="px-3 py-2 border border-slate-300 rounded-md bg-white text-sm"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              const d = new Date();
              const yyyy = d.getFullYear();
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const dd = String(d.getDate()).padStart(2, '0');
              setSelectedDate(`${yyyy}-${mm}-${dd}`);
            }}
            data-testid="map-today-btn"
          >
            Aujourd&apos;hui
          </Button>
          <span data-testid="intervention-count" className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"><MapPin size={14} className="inline mr-1" /> {items.length} intervention(s)</span>
          {geocoding > 0 && <span className="px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-sm">⏳ Géolocalisation en cours ({geocoding})...</span>}
          <span className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm">📍 {markers.length} localisée(s)</span>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" style={{ height: 'calc(100vh - 220px)' }}>
          {loading ? (
            <div className="flex items-center justify-center h-full">Chargement...</div>
          ) : (
            <MapContainer
              center={DEFAULT_CENTER}
              zoom={10}
              scrollWheelZoom={true}
              style={{ width: '100%', height: '100%' }}
              data-testid="leaflet-map"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {markers.map(({ it, pos }) => (
                <Marker key={it.id} position={[pos.lat, pos.lng]} icon={it.done ? doneIcon : defaultIcon}>
                  <Popup>
                    <div className="text-sm">
                      <div className="font-bold mb-1">{it.client_name}</div>
                      <div className="text-slate-600 mb-1">{it.client_address}</div>
                      {it.client_phone && (
                        <div className="mb-1"><a href={`tel:${it.client_phone}`} className="text-blue-600">{it.client_phone}</a></div>
                      )}
                      <div className="text-xs text-slate-500 mb-2">
                        {it.done ? <><CheckCircle size={12} className="inline mr-1 text-green-600" /> Effectuée</> : <><Clock size={12} className="inline mr-1" /> {fmtDate(it.scheduled_date)}</>}
                      </div>
                      {it.request_description && (
                        <div className="text-xs text-slate-700 mb-2">{it.request_description.slice(0, 100)}{it.request_description.length > 100 ? '...' : ''}</div>
                      )}
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${pos.lat},${pos.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs inline-flex items-center gap-1 bg-blue-600 text-white px-2 py-1 rounded"
                      >
                        <ArrowSquareOut size={12} /> Itinéraire
                      </a>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </div>

        {!loading && items.length === 0 && (
          <div className="mt-4 text-center text-slate-500 italic">Aucune intervention prévue pour cette date.</div>
        )}
      </div>
    </div>
  );
};

export default InterventionMap;
